import React, { useState } from 'react';
import { X, Crown, Mail, Sparkles, Wheat, BookOpen, Beaker, ChevronRight, Stethoscope, Trophy, Calculator} from 'lucide-react';
import { sendTrialEndedEmail } from '../../lib/sendEmail';
import { auth } from '../../lib/firebase';
import BreadflowIcon from '../layout/BreadflowIcon.tsx';
import { shouldBypassSubscription } from '../../lib/platform';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SubscriptionModal({ isOpen, onClose }: SubscriptionModalProps) {
  const [loading, setLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  

  const handleSendEmail = async () => {
    if (!auth.currentUser?.email) {
      setEmailError('No email address found');
      return;
    }
    
    try {
      setEmailError(null);
      setEmailSending(true);
      
      await sendTrialEndedEmail(auth.currentUser.email);
      
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 5000);
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Failed to send email. Please try again later.';
      setEmailError(errorMessage);
    } finally {
      setEmailSending(false);
    }
  };

  // Never show subscription modal on WebViewGold iOS and Safari iOS/iPad (App Store compliance)
  if (shouldBypassSubscription()) return null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg w-full max-w-2xl overflow-hidden">
        <div className="p-6 border-b border-white/10 flex justify-between items-center relative">
          <div className="flex items-center gap-3 w-full justify-left">
            <h2 className="text-2xl text-white font-playfair font-light italic">You've reached the crust...</h2>
          </div>
          <button 
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full absolute right-4"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-8">
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <Crown className="w-8 h-8 text-amber-400 fill-amber-400" />
              </div>
              
              <h3 className="text-2xl text-white font-playfair font-light italic">Ready for the crumb?</h3>
              
              <p className="text-lg text-white/90 max-w-md mx-auto">
                There's so much more you could explore with Breadlify — and bake even better bread!
              </p>
            
              <div className="bg-white/5 p-6 rounded-xl mt-8">
                <div className="text-center mb-4">
                  <h3 className="text-xl text-white font-brand font-light italic">Want more info?</h3>
                  <p className="text-sm text-white/80 mt-2">We'll send you an email with all the details.</p>
                </div>
                 
               {emailSent && (
                 <div className="p-3 mb-4 bg-green-500/20 rounded-lg text-green-200 text-sm text-center">
                   <div className="flex items-center justify-center gap-2">
                     <div className="w-4 h-4 bg-green-400 rounded-full"></div>
                     Email sent successfully! Please check your inbox.
                   </div>
                 </div>
               )}
               
               {emailError && (
                 <div className="p-3 mb-4 bg-red-500/20 rounded-lg text-red-200 text-sm text-center">
                   {emailError}
                 </div>
               )}

               <button
                 onClick={handleSendEmail}
                 disabled={emailSending || emailSent}
                 className={`w-full py-3 mb-3 border border-white/20 hover:bg-white/10 text-white rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${
                   emailSending || emailSent ? 'opacity-70 cursor-not-allowed' : 'hover:border-amber-400'
                 }`}
               >
                 {emailSending ? (
                   <>
                     <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                     Sending...
                   </>
                 ) : (
                   <>
                     <Mail className="w-5 h-5 text-amber-400" />
                     Send Me Information
                     <ChevronRight className="w-4 h-4 ml-1" />
                   </>
                 )}
               </button>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
}